import React, { useEffect, Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import { connect } from "react-redux";

import routes, { agentBaseName, smeBaseName } from "./routes";

import { FormLayout } from "./containers/FormLayout";
import { ServerRequestLoadingScreen } from "./components/ServerRequestLoadingScreen/ServerRequestLoadingScreen";

import {
  ProspectProtectedRoute,
  OTPProtectedRoute,
  AccountTypeProtectedRoute,
  ProtectedRoute,
  OTPGeneratedProtectedRoute,
  AgentProtectedRoute
} from "./containers/Routers";

import { receiveAppConfig } from "./store/actions/appConfig";
import { prospectAutoSave } from "./store/actions/sendProspectToAPI";
import { EFROTPVerification } from "./containers/ComeBackVerification";
import { SEOManager } from "./components/SEO";

import "./App.scss";
import SessionExpiration from "./containers/Session";

import { FormEmailConfirmPage, FormConfirmMobilePage } from "./containers/FormConfirm";
import { PartnerOTPVerification } from "./containers/MultiPatnerJourney/PartnerVerification/PartnerOTPVerification";
import { RoInviteEFRSuccessScreen } from "./containers/CompanyStakeholders/components/StakeholderTermsAndConditions/RoInviteEFRSuccessScreen";
const QuickapplyLanding = lazy(() => import("./containers/AccountsComparison"));
const ApplicantInfo = lazy(() => import("./containers/ApplicantInfo"));
const DetailedAccount = lazy(() => import("./containers/DetailedAccount"));
const DulServices = lazy(() => import("./containers/DulServices"));
const DulConsentWaitingScreen = lazy(() => import("./containers/DulConsentWaitingScreen"));
const CompanyInfo = lazy(() => import("./containers/CompanyInfo"));
const PartnershipPage = lazy(() => import("./containers/MultiPatnerJourney/PartnershipPage"));
const PartnerDetail = lazy(() => import("./containers/MultiPatnerJourney/PartnershipDetail"));
const PartnersSummary = lazy(() => import("./containers/MultiPatnerJourney/PartnersSummary"));
const CompanyStakeholders = lazy(() => import("./containers/CompanyStakeholders"));
const StakeholderPreview = lazy(() =>
  import("./containers/CompanyStakeholders/StakeholderPreview")
);
const StakeholderTermsAndConditions = lazy(() =>
  import("./containers/CompanyStakeholders/components/StakeholderTermsAndConditions")
);
const AdditionalInfoComponent = lazy(() => import("./containers/Additional"));
const ApplicationOverview = lazy(() => import("./containers/ApplicationOverview"));
const ComeBackLogin = lazy(() => import("./containers/ComeBackLogin"));
const ComeBackVerification = lazy(() => import("./containers/ComeBackVerification"));
const MyApplications = lazy(() => import("./containers/MyApplications"));
const NotFoundPage = lazy(() => import("./containers/NotFoundPage"));
const PersonaSelection = lazy(() => import("./containers/PersonaSelection"));
const WebToMobilePage = lazy(() => import("./containers/WebToMobilePage"));
const ApplicationInvitation = lazy(() => import("./containers/ApplicationInvitation"));
const EFRInvitation = lazy(() => import("./containers/EFRInvitation"));
const AdditionalCompanyInformation = lazy(() =>
  import("./containers/Additional/components/CompanyInformation")
);
const AdditionalStakeholderInformation = lazy(() =>
  import("./containers/Additional/components/StakeholderInformation")
);
const AccountServices = lazy(() => import("./containers/AccountServices"));
const ReviewAndSubmit = lazy(() => import("./containers/ReviewAndSubmit"));
const CongratulationsScreen = lazy(() => import("./containers/Congratulations"));
const AdditionalInformation = lazy(() => import("./containers/AdditionalInformation"));
const AdditionaInfoInvite = lazy(() =>
  import("./containers/AdditionalInformation/AdditionaInfoInvite")
);
const SearchItemRedirect = lazy(() =>
  import("./containers/AgentPages/SearchProspect/components/SearchResults/SearchItemRedirect")
);
const Login = lazy(() => import("./containers/AgentPages/Login"));
const Admin = lazy(() => import("./containers/AgentPages/SearchProspect"));
const SearchedAppInfo = lazy(() => import("./containers/AgentPages/SearchedAppInfo"));
const InviteCustomer = lazy(() => import("./containers/AgentPages/InviteCustomer"));
const RestartOTP = lazy(() => import("./containers/Otp"));
const PartnerInvitation = lazy(() => import("./containers/MultiPatnerJourney/PartnerVerification"));
const PartnerAndCompanyVerification = lazy(() =>
  import("./containers/MultiPatnerJourney/PartnerAndCompanyVerification")
);

const App = ({ receiveAppConfig, prospectAutoSave }) => {
  useEffect(() => {
    receiveAppConfig();
    prospectAutoSave();
  }, [receiveAppConfig, prospectAutoSave]);

  return (
    <FormLayout>
      <SEOManager />
      <Suspense fallback={ServerRequestLoadingScreen()}>
        <Routes>
          <Route
            exact
            path={routes.applicationOverview}
            element={
              <AccountTypeProtectedRoute>
                <ApplicationOverview />
              </AccountTypeProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.personaSelection}
            element={
              <AccountTypeProtectedRoute>
                <PersonaSelection />
              </AccountTypeProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.applicantInfo}
            element={
              <ProtectedRoute>
                <ApplicantInfo />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.verifyEmailOtp}
            element={
              <ProspectProtectedRoute>
                <FormEmailConfirmPage />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.verifyMobileOtp}
            element={
              <ProspectProtectedRoute>
                <FormConfirmMobilePage />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.companyInfo}
            element={
              <ProspectProtectedRoute>
                <CompanyInfo />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.dulServices}
            element={
              <ProspectProtectedRoute>
                <DulServices />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.dulConsentInfo}
            element={
              <ProspectProtectedRoute>
                <DulConsentWaitingScreen />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.patnershipDetails}
            element={
              <ProspectProtectedRoute>
                <PartnershipPage />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.patnerDetails}
            element={
              <ProspectProtectedRoute>
                <PartnerDetail />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.partnersSummary}
            element={
              <ProspectProtectedRoute>
                <PartnersSummary />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.stakeholdersInfo}
            element={
              <ProspectProtectedRoute>
                <CompanyStakeholders />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.stakeholdersPreview}
            element={
              <ProspectProtectedRoute>
                <StakeholderPreview />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.StakeholderTermsAndConditions}
            element={
              <ProspectProtectedRoute>
                <StakeholderTermsAndConditions />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.roInviteEFRSuccess}
            element={
              <ProspectProtectedRoute>
                <RoInviteEFRSuccessScreen />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.additionalInfoComponent}
            element={
              <ProspectProtectedRoute>
                <AdditionalInfoComponent />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.additionalCompanyInformation}
            element={
              <ProspectProtectedRoute>
                <AdditionalCompanyInformation />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.additionalStakeholderInformation}
            element={
              <ProspectProtectedRoute>
                <AdditionalStakeholderInformation />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.accountServices}
            element={
              <ProspectProtectedRoute>
                <AccountServices />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.reviewAndSubmit}
            element={
              <ProspectProtectedRoute>
                <ReviewAndSubmit />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.congratulations}
            element={
              <ProspectProtectedRoute>
                <CongratulationsScreen />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.detailedAccount}
            element={
              <AccountTypeProtectedRoute>
                <DetailedAccount />
              </AccountTypeProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.MyApplications}
            element={
              <OTPProtectedRoute>
                <MyApplications />
              </OTPProtectedRoute>
            }
          />
          <Route
            path={routes.comeBackLogin}
            element={
              <ProtectedRoute>
                <ComeBackLogin />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.comeBackLoginVerification}
            element={
              <OTPGeneratedProtectedRoute>
                <ComeBackVerification />
              </OTPGeneratedProtectedRoute>
            }
          />
          <Route
            path={routes.quickapplyLanding}
            element={
              <ProtectedRoute>
                <QuickapplyLanding />
              </ProtectedRoute>
            }
          />
          <Route
            path={smeBaseName}
            element={
              <ProtectedRoute>
                <QuickapplyLanding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <QuickapplyLanding />
              </ProtectedRoute>
            }
          />
          <Route path={routes.webToMobile} element={<WebToMobilePage />} />
          <Route path={routes.applicationInvitation} element={<ApplicationInvitation />} />
          <Route path={routes.efrInvite} element={<EFRInvitation />} />
          <Route path={routes.additionalDetails} element={<AdditionaInfoInvite />} />
          <Route path={routes.partnerVerification} element={<PartnerInvitation />} />
          <Route
            path={routes.companyAndPartnerOTPVerification}
            element={<PartnerAndCompanyVerification />}
          />
          <Route exact path={routes.searchItemRedirect} element={<SearchItemRedirect />} />
          <Route
            path={routes.additionalInformation}
            element={
              <ProspectProtectedRoute>
                <AdditionalInformation />
              </ProspectProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.efrOTPVerification}
            element={
              <OTPGeneratedProtectedRoute>
                <EFROTPVerification />
              </OTPGeneratedProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.partnerOTPVerification}
            element={
              <OTPGeneratedProtectedRoute>
                <PartnerOTPVerification />
              </OTPGeneratedProtectedRoute>
            }
          />
          {/* ----Agent routes--- */}
          <Route exact path={agentBaseName} element={<Login />} />
          <Route exact path={routes.login} element={<Login />} />
          <Route
            exact
            path={routes.inviteCustomer}
            element={
              <AgentProtectedRoute>
                <InviteCustomer />
              </AgentProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.searchProspect}
            element={
              <AgentProtectedRoute>
                <Admin />
              </AgentProtectedRoute>
            }
          />
          <Route
            exact
            path={routes.SearchedAppInfo}
            element={
              <AgentProtectedRoute>
                <SearchedAppInfo />
              </AgentProtectedRoute>
            }
          />
          {/* ----Agent routes--- */}
          <Route
            path={routes.restartOtp}
            element={
              <ProspectProtectedRoute>
                <RestartOTP />
              </ProspectProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <SessionExpiration />
    </FormLayout>
  );
};

const mapDispatchToProps = {
  receiveAppConfig,
  prospectAutoSave
};

export default connect(
  null,
  mapDispatchToProps
)(App);
